import { useEffect } from 'react';
import type { ReactNode, Ref } from 'react';
import { Box, Typography } from '@mui/material';
import { pageHeaderStore, useInShell } from '../app/pageHeader';

export interface PageHeaderProps {
  /** Page title (sentence case; caller provides translated copy). */
  title: string;
  /** Optional supporting subtitle. */
  subtitle?: ReactNode;
  /** Right-aligned actions (buttons, filters). */
  actions?: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Declares the page header. Under the authenticated shell the title/subtitle/
 * actions render in the sticky {@link import('../app/TopBar').TopBar} (prototype
 * chrome), so this registers into the page-header store and renders nothing.
 * Standalone (tests, public pages) it renders the header inline so it is still
 * visible. Titles are the prototype's 700 / 23px Inter treatment.
 */
export function PageHeader({ title, subtitle, actions, ref }: PageHeaderProps) {
  const inShell = useInShell();

  // Keep the store fresh on every render so dynamic actions stay current…
  useEffect(() => {
    if (!inShell) {
      return;
    }
    pageHeaderStore.set({ title, subtitle, actions });
  });

  // …and clear it only when the page unmounts.
  useEffect(() => {
    if (!inShell) {
      return;
    }
    return () => pageHeaderStore.set(null);
  }, [inShell]);

  if (inShell) {
    return null;
  }

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: (t) => t.typography.button.fontFamily,
            fontWeight: 700,
            fontSize: 23,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400, mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{actions}</Box>
      )}
    </Box>
  );
}
